import type { SchedulingAvailabilityInfo } from '@carvana/merch-contracts';
import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { Result, ResultStatus } from '@carvana/merch-result';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { DeepPartial } from '~/types';
import selectSoonestRoute from '~/utilities/vdp/selectSoonestRoute';

export type DeliveryPickupContent = {
  soonestDelivery: Partial<Result<SchedulingAvailabilityInfo>>;
  soonestPickup: Partial<Result<SchedulingAvailabilityInfo>>;
  vehicleId: number;
};

export type ContextType = {
  deliveryPickup: LoadableState<DeliveryPickupContent>;
  fetchDeliveryPickupFlag: number;
  triggerFetchDeliveryPickup: () => void;
};

const defaultContext: ContextType = {
  deliveryPickup: defaultLoadableState({
    vehicleId: 0,
    soonestDelivery: {},
    soonestPickup: {}
  }),
  fetchDeliveryPickupFlag: 0,
  triggerFetchDeliveryPickup: () => {}
};

const DeliveryPickupContext = createContext<ContextType>(defaultContext);

export function DeliveryPickupContextProvider({
  children
}: React.PropsWithChildren<{}>) {
  const deliveryPickup = useLoadableState<DeliveryPickupContent>(
    defaultContext.deliveryPickup.data
  );
  const [fetchDeliveryPickupFlag, setFetchDeliveryPickupFlag] = useState(
    defaultContext.fetchDeliveryPickupFlag
  );

  const triggerFetchDeliveryPickup = useCallback(
    () => setFetchDeliveryPickupFlag(current => current + 1),
    []
  );

  const value = useMemo<ContextType>(
    () => ({
      deliveryPickup,
      fetchDeliveryPickupFlag,
      triggerFetchDeliveryPickup
    }),
    [deliveryPickup, fetchDeliveryPickupFlag, triggerFetchDeliveryPickup]
  );

  return (
    <DeliveryPickupContext.Provider value={value}>
      {children}
    </DeliveryPickupContext.Provider>
  );
}

DeliveryPickupContextProvider.displayName = 'DeliveryPickupContextProvider';

export default DeliveryPickupContext;

export const generateDeliveryPickupContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useDeliveryPickup(): ContextType['deliveryPickup'] {
  return useContext(DeliveryPickupContext).deliveryPickup;
}

export function useDeliveryInfo(): SchedulingAvailabilityInfo | undefined {
  const delivery = useDeliveryPickup().data.soonestDelivery;

  return delivery.status === ResultStatus.Succeeded
    ? delivery.content
    : undefined;
}

export function usePickupInfo(): SchedulingAvailabilityInfo | undefined {
  const pickup = useDeliveryPickup().data.soonestPickup;

  return pickup.status === ResultStatus.Succeeded ? pickup.content : undefined;
}

export function useSoonestRoute(): SchedulingAvailabilityInfo | undefined {
  const delivery = useDeliveryInfo();
  const pickup = usePickupInfo();

  return selectSoonestRoute(delivery, pickup);
}
